import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { Button } from "@ui/shadCN/component/button";
import TextareaAutosize from "react-textarea-autosize";
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
} from "@ui/shadCN/component/input-group";
import { inputLengthCount, inputWarnColor } from "@utils/ui";

import { isMonitoringAPIError } from "pages/monitoring/api/types/guards";
import { ErrorsList, LabelAndErrors } from "@ui/LabelingWithErrors";
import { invitationValidations } from "pages/monitoring/outlets/myProfile/initiativeJoinInvitation/utils/formClientValidations";
import { StrValidator } from "@utils/strValidator";
import { INITIATIVE_INVITATION_MESSAGE_MAX_LENGTH } from "@config/monitoring";
import { makeInitialInfo } from "pages/monitoring/outlets/myProfile/initiativeJoinInvitation/utils/formObjectUpdate";
import type {
  JoinInitiativeDataForm,
  JoinInitiativeGuest,
  JoinInitiativeDataFormErr,
} from "pages/monitoring/types/userJoinRequest";
import { validateFormClient } from "pages/monitoring/ui/initiativesAdmin/utils/validateFormClient";
import { uiText } from "pages/monitoring/outlets/myProfile/initiativeJoinInvitation/layout/uiText";
import { sendJoinInitiativeInvitation } from "pages/monitoring/api/services/initiatives";
import { SendedInvitations } from "pages/monitoring/outlets/myProfile/initiativeJoinInvitation/SendedInvitations";

export function InitiativeInvitationForm({
  initiativeId,
}: {
  initiativeId: number;
}) {
  const [errors, setErrors] = useState<Partial<JoinInitiativeDataFormErr>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [guestEmails, setGuestEmails] = useState<string>("");
  const [customMessage, setCustomMessage] = useState<string>("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [formData, setFormData] = useState<JoinInitiativeDataForm>(
    makeInitialInfo(initiativeId),
  );
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  const validateField = useCallback(
    (
      fieldName: keyof JoinInitiativeDataForm,
      validation: StrValidator,
      onClean?: (cleanValue: string) => void,
    ) => {
      const [cleanValue, fieldErrors] = validation.result;

      if (fieldErrors.length > 0) {
        setErrors((oldErr) => ({ ...oldErr, [fieldName]: fieldErrors }));
        return;
      }

      setErrors(({ [fieldName]: _, ...oldErr }) => oldErr);
      if (onClean) {
        onClean(cleanValue);
      }
    },
    [],
  );

  const handleFormReset = () => {
    setFormData(makeInitialInfo(initiativeId));
    setGuestEmails("");
    setCustomMessage("");
    setErrors({});
    setMessage(null);
  };

  const messageOnBlur = () =>
    validateField(
      "message",
      new StrValidator(customMessage)
        .isOptional()
        .sanitize()
        .hasLengthLessOrEqualThan(INITIATIVE_INVITATION_MESSAGE_MAX_LENGTH),
      (val) => {
        setCustomMessage(val);
        setFormData((old) => ({ ...old, message: val }));
      },
    );

  const emailsOnBlur = () => {
    validateField(
      "guests",
      new StrValidator(guestEmails)
        .sanitize()
        .isRequired()
        .custom((val) => {
          const emailList = val
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email !== "");

          if (emailList.length === 0) {
            return false;
          }

          return emailList.every((email) => {
            const [, errors] = new StrValidator(email).isEmail().result;
            return errors.length === 0;
          });
        }, uiText.form.validation.badEmailFormat),
      (val) => {
        setGuestEmails(val);
        const emailList = val
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email !== "");

        setFormData((old) => ({
          ...old,
          guests: emailList.map((email) => ({ email }) as JoinInitiativeGuest),
        }));
      },
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    emailsOnBlur();
    messageOnBlur();

    const currentErrors = validateFormClient(formData, invitationValidations);
    setErrors(currentErrors);

    if (Object.keys(currentErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    const payload: JoinInitiativeDataForm = {
      initiativeId: initiativeId,
      message: formData.message,
      guests: formData.guests,
    };

    const res = await sendJoinInitiativeInvitation(payload);

    if (isMonitoringAPIError(res)) {
      setErrors((oldErr) => ({
        ...oldErr,
        root: res.data.map((error) => error.msg),
      }));
      setIsLoading(false);
      return;
    }

    handleFormReset();
    setRefreshTrigger((old) => old + 1);
    setMessage({ text: uiText.success, error: false });
    setIsLoading(false);
  };

  return (
    <div className="w-full rounded-xl p-6 shadow-sm flex flex-col gap-4 border border-muted">
      <h4 className="text-primary m-0! mb-2 text-lg font-semibold">
        {uiText.title}
      </h4>

      <form
        action=""
        onReset={handleFormReset}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-4"
      >
        <div>
          <LabelAndErrors
            htmlFor="email"
            errID="errors_guests"
            validationErrors={errors.guests ?? []}
          >
            {uiText.form.emailsLabel}
            <span aria-hidden="true">*</span>
          </LabelAndErrors>
          <InputGroup>
            <InputGroupInput
              id="email"
              type="text"
              placeholder="ejemplo1@correo.com, ejemplo2@correo.com"
              value={guestEmails}
              onChange={(e) => setGuestEmails(e.target.value)}
              onBlur={emailsOnBlur}
              disabled={isLoading}
              aria-invalid={errors.guests !== undefined}
              aria-describedby={errors.guests ? "errors_guests" : undefined}
            />
          </InputGroup>
        </div>

        <div>
          <LabelAndErrors
            htmlFor="message"
            errID="errors_message"
            validationErrors={errors.message ?? []}
          >
            {uiText.form.messageLabel}
          </LabelAndErrors>
          <InputGroup>
            <TextareaAutosize
              data-slot="input-group-control"
              className="flex field-sizing-content min-h-16 w-full resize-none rounded-md bg-transparent px-3 py-2.5 text-base transition-[color,box-shadow] outline-none md:text-sm"
              id="message"
              placeholder="Escribe un mensaje..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              onBlur={messageOnBlur}
              rows={3}
              disabled={isLoading}
              aria-describedby={errors.message ? "errors_message" : undefined}
              maxLength={INITIATIVE_INVITATION_MESSAGE_MAX_LENGTH}
            />
            <InputGroupAddon
              align="block-end"
              className={`${inputWarnColor(customMessage, INITIATIVE_INVITATION_MESSAGE_MAX_LENGTH, 0.95)} flex-row-reverse`}
            >
              {inputLengthCount(
                customMessage,
                INITIATIVE_INVITATION_MESSAGE_MAX_LENGTH,
              )}
            </InputGroupAddon>
          </InputGroup>
        </div>

        {message && !message.error && <p>{message.text}</p>}

        <ErrorsList errorItems={errors.root ?? []} />

        <div className="flex flex-row-reverse flex-wrap justify-between gap-4 mt-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? uiText.loading : uiText.save}
          </Button>
          <Button
            type="reset"
            variant="outline_destructive"
            disabled={isLoading}
          >
            {uiText.restartForm}
          </Button>
        </div>
      </form>

      <hr className="m-2 mt-4 border-primary/20" />

      <h4 className="text-primary m-0! mb-2 text-lg font-semibold">
        {uiText.sendedInvitationsTitle}
      </h4>
      <SendedInvitations
        initiativeId={initiativeId}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
}
